<template>
  <div
    class="TouchNumber select-none"
    :class="{
      'flex': !inline,
      'inline-flex': inline,
      'h-28': design === 'xs',
      'h-32': design === 'sm',
      'h-36': design === 'md',
      'h-40': design === 'base',
      'h-48': design === 'lg',
      'pointer-events-none bg-bg-inactive text-text-inactive': disableEdit
    }"
  >
    <button
      class="flex items-center justify-center shrink-0 grow-0
      border-l border-y border-border-base group hover:bg-bg-inverse hover:border-bg-inverse relative transition-colors"
      :class="{
        touchOnly: !showButtonsOnDesktop,
        disabled: loading || disabled || isMin,
        'pointer-events-none': value < 1 || isMin,
        'h-28 w-28 basis-28': design === 'xs',
        'h-32 w-32 basis-32': design === 'sm',
        'h-36 w-36 basis-36': design === 'md',
        'h-40 w-40 basis-40': design === 'base',
        'h-48 w-48 basis-48': design === 'lg',
      }"
      type="button"
      :aria-label="$t('wcag.arialabel.decrease')"
      :disabled="loading || disabled || value < 1 || isMin"
      @click="change(-1)"
    >
      <img
        src="/icons/minus.svg"
        class="
        w-16 h-16 outline outline-8 outline-transparent transition-all
        group-hover:invert"
        :class="{
          'opacity-50': value < 1,
        }"
        alt=""
      >
    </button>
    <div
      class="border border-border-base"
      :class="[inputBasis]"
    >
      <div
        v-if="loading && noInputField"
        class="miniNumber flex items-center justify-center"
        :class="{
          'w-28 h-28': design === 'xs',
          'w-32 h-32 desk:h-40': design !== 'xs',
        }"
      >
        <LoadingIcon />
      </div>
      <div
        v-else-if="noInputField"
        class="miniNumber w-full  flex items-center justify-center"
        :class="{
          'h-28 type-sm': design === 'xs',
          'h-32 type-sm': design === 'sm',
          'h-36 w-36 basis-36': design === 'md',
          'h-40 w-40 basis-40': design === 'base',
          'h-48 w-48 basis-48': design === 'lg',
        }"
      >
        {{ value }}
      </div>
      <div v-else class="w-full h-full relative">
        <span
          v-if="props.prefix"
          class="absolute left-1/2 top-1/2 type-headline-sm text-text-subtle pointer-events-none select-none"
          :style="{ transform: 'translateX(calc(50% + -34px)) translateY(-50%)' }"
        >
          {{ props.prefix }}
        </span>
        <input
          :id="undefined"
          v-model="value"
          type="number"
          :placeholder="placeholder"
          class="w-full fastOrder pl-8 select-auto text-text-base type-sm"
          :class="{
            hideBrowserUpDownButtons: showButtonsOnDesktop,
            'h-full': !showUnits || (showUnits && design !== 'lg' && design !== 'base'),
            'pt-4': showUnits && design === 'lg',
            'type-sm relative': design === 'xs',
            'h-full top-[-2px]': design === 'base',
            'h-full top-[-1px] type-sm relative !bg-transparent': design === 'sm' && showUnits,
            '!text-center px-4': !showUnits,
            'pointer-events-none !bg-bg-inactive !text-text-inactive': disableEdit
          }"
          min="1"
          :tabindex="typeof index === 'number' ? (index * 10) +1 : undefined"
          v-bind="attr"
          :disabled="loading || disabled"
          @keydown.enter="emit('enter')"
          @blur="emit('blurred')"
          @keyup="emit('change')"
          @input="emit('change')"
        >
        <span
          v-if="props.suffix"
          class="absolute right-1/2 top-1/2 type-headline-sm text-text-subtle pointer-events-none select-none"
          :style="{ transform: 'translateX(calc(50% + 34px)) translateY(-50%)' }"
        >
          {{ props.suffix }}
        </span>
        <div
          v-if="showUnits !== ''"
          class="TouchNumberSuffix absolute type-sm leading-single text-text-subtle pointer-events-none"
          :class="{
            'top-[16px] right-12': design === 'lg',
            'top-[13px] desk:top-[12px] right-8': design === 'base',
            'top-[11px] right-8 type-xs': design === 'md',
            'top-[8px] desk:top-[7px] right-[8px]': design === 'sm',
            'top-[7px] right-8 type-xs': design === 'xs',
            'desk:!right-[20px]': !showButtonsOnDesktop,
          }"
        >
          {{ props.slashBeforeUnits ? '/' + showUnits : showUnits }}
        </div>
      </div>
    </div>
    <button
      class="shrink-0 grow-0
      flex items-center justify-center
      border-r border-y border-border-base group hover:bg-bg-inverse hover:border-bg-inverse relative transition-colors"
      :class="{
        touchOnly: !showButtonsOnDesktop,
        disabled: loading || disabled,
        'h-28 w-28 basis-28': design === 'xs',
        'h-32 w-32 basis-32': design === 'sm',
        'h-36 w-36 basis-36': design === 'md',
        'h-40 w-40 basis-40': design === 'base',
        'h-48 w-48 basis-48': design === 'lg',
        'pointer-events-none bg-bg-inactive text-text-inactive !top-0': disableEdit
      }"
      type="button"
      :aria-label="$t('wcag.arialabel.increase')"
      :disabled="loading || disabled || isMax || disableEdit"
      @click="change(1)"
    >
      <img
        src="/icons/plus.svg"
        class="
        w-16 h-16 group-hover:invert"
        alt=""
      >
    </button>
  </div>
</template>

<script setup lang="ts">
/**
 * Usage:
 * Minimal:
 * <TouchNumber v-model="touch1" />
 *
 * Adding any attributes, like min/max as object
 * <TouchNumber
 *    v-model="touch1"
 *    :attr="{ class: 'customClass', max: 100, min: 0, step: 1 }"
 *  />
 */
const value = defineModel<number>({ required: true });

const props = withDefaults(defineProps<{
  index?: number | null,
  id?: string | null,
  attr?: {
    [key: string]: string | number;
  };
  showButtonsOnDesktop?: boolean;
  noInputField?: boolean;
  loading?: boolean;
  showUnits?: string | null;
  design?: 'lg' | 'base' | 'md' | 'sm' | 'xs';
  inline?: boolean;
  minWidth?: number;
  disabled?: boolean;
  prefix?: string | null;
  suffix?: string | null;
  disableEdit?: boolean;
  slashBeforeUnits?: boolean;
  placeholder?: string;
}>(), {
  index: null,
  id: null,
  showUnits: null,
  design: 'base',
  inline: false,
  minWidth: -1,
  prefix: null,
  suffix: null,
  disableEdit: false,
  slashBeforeUnits: false,
  placeholder: '',
});

const emit = defineEmits<{
  (e: 'enter'): void;
  (e: 'blurred'): void;
  (e: 'change'): void;
}>();

const inputBasis = computed(()=> {
  if (!props.inline) {
    return 'basis-full';
  }
  if (props.minWidth > -1) {
    return `w-[${props.minWidth}px] basis-[${props.minWidth}px]`;
  }

  switch (props.design) {
    case 'lg':
      return 'w-[64px] basis-[64px]';
    case 'base':
      return 'w-[52px] basis-[52px]';
    case 'md':
      return 'w-[48px] basis-[48px]';
    case 'sm':
      return 'w-[40px] basis-[40px]';
    case 'xs':
      return 'w-[40px] basis-[40px]';
  }

  return 'w-[40px] basis-[40px]';
});

const change = (dir: number) => {
  const newNumber = value.value + dir;
  if (typeof props.attr?.max === 'number' && newNumber > props.attr.max) {
    return;
  }

  const min = typeof props.attr?.min === 'number' ? props.attr.min : 1;
  if (newNumber >= min) {
    value.value = newNumber;
  }
  emit('change');
};

const isMin = computed(()=> {
  if (typeof props.attr?.min === 'number') {
    return value.value <= props.attr.min;
  }
  return false;
});

const isMax = computed(()=> {
  if (typeof props.attr?.max === 'number') {
    return value.value >= props.attr.max;
  }
  return false;
});

</script>

<style scoped lang="postcss">
.miniNumber {
  @apply leading-single rounded-0;
}

@media (pointer: coarse) { /*  touch */
  .miniNumber {

    @apply text-center;
    &::-webkit-inner-spin-button, &::-webkit-outer-spin-button {
      @apply hidden;
    }
  }
}

@media (pointer: fine) {
  .miniNumber {
    @apply text-center;
    &::-webkit-inner-spin-button, &::-webkit-outer-spin-button {
      @apply opacity-100;
    }
  }
  .touchOnly {
    @apply hidden;
  }
}

input[type="number"].hideBrowserUpDownButtons {
  appearance: textfield;
}

input[type="number"].hideBrowserUpDownButtons::-webkit-inner-spin-button,
input[type="number"].hideBrowserUpDownButtons::-webkit-outer-spin-button {
  @apply hidden;
}

.fastOrder {
  @apply leading-single rounded-0;
}

@media (pointer: coarse) { /*  touch */
  .fastOrder {
    @apply text-left;
  }
}

@media (pointer: fine) {
  .fastOrder {
    @apply text-left;
    &::-webkit-inner-spin-button, &::-webkit-outer-spin-button {
      @apply opacity-100;
    }
  }
}

</style>
